import React, { useCallback, useContext, useMemo } from "react";
import {
  Modal,
  Alert,
  Collapse,
  Divider,
  DatePicker,
  Image,
  Input,
  Radio,
  Select,
  Space,
  Spin,
  Steps,
  Tooltip,
} from "antd";
import { VolSurfaceContext } from "contexts/VolSurfaceContext";
import {
  PrimaryButton,
  SecondaryButton,
  CancelButton,
} from "components/Common/CustomButtons/CustomButtons";
import VolCalcFailedModal from "components/Common/VolCalcFailedModal/VolCalcFailedModal";
import { GlobalContext } from "contexts/GlobalContext";
import {
  OTHER_SURFACE_LADDER_COLUMNS,
  TRADE_CAPTURE_OUTPUT,
} from "constants/tradeCapture";
import {
  isOtherCallPutImport,
  formatProductContractStyleLabel,
  normalizeProductContractStyleIdValue,
  withOtherSurfaceUnderlying,
} from "helpers/tradeCaptureHelper";
import { normalizeProductContractStyleId } from "components/VolSurface/StickyStrike/hooks/stickyStrikeRequestUtils";
import useDraggableModal from "hooks/useDraggableModal";
import UploadForm from "./UploadForm";
import TradeTable from "./TradeTable";
import OtherSurfacePreview from "./OtherSurfacePreview";
import "./TradeCapture.scss";
import useTradeCaptureModalController, {
  TRADE_CAPTURE_DESTINATION,
} from "./useTradeCaptureModalController";

const { Step } = Steps;

const OPTION_STEPS = [
  { title: "Upload", description: "Screenshot" },
  { title: "Review", description: "Edit table" },
  { title: "Validate", description: "Check data" },
  { title: "Import", description: "Destination" },
];

const OTHER_SURFACE_STEPS = [
  { title: "Upload", description: "Screenshot" },
  { title: "Review", description: "Edit ladder + UL" },
  { title: "Preview", description: "Current vs new" },
  { title: "Fill", description: "Other surface" },
];

/**
 * Screenshot import popup. `output` selects the flow (see
 * useTradeCaptureModalController): OPTIONS for Pricer Option Capture, or
 * OTHER_SURFACE to fill one tenor of the Other surface from an option chain.
 */
const TradeCaptureModal = ({
  open,
  onClose,
  refetchOptionRow,
  output = TRADE_CAPTURE_OUTPUT.OPTIONS,
  onOtherSurfaceFilled,
  onSurfaceLocked,
}) => {
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  // Option Capture (Pricer) renders this modal outside VolSurfaceContext.
  const volSurfaceFilterShortcuts =
    useContext(VolSurfaceContext)?.state?.volSurfaceFilterShortcuts ?? null;

  const {
    session,
    source,
    setSource,
    loading,
    addingScreenshot,
    validating,
    importing,
    validationErrors,
    importDestination,
    setImportDestination,
    importDate,
    setImportDate,
    isBusy,
    stepsCurrent,
    volCalcFailedInfo,
    confirmImportSkippingVol,
    dismissVolCalcFailed,
    requestClose,
    handleUpload,
    handleApprove,
    handleImport,
    handleTableUpdate,
    isOtherSurface,
    normalizeRows,
    underlying,
    handleUnderlyingChange,
    notices,
    otherSurfacePreview,
    previewing,
    filling,
    handlePreview,
    dismissOtherSurfacePreview,
    handleFill,
    otherSurfaceProductId,
    otherSurfaceProductStyle,
    otherSurfaceProductLabel,
    handleOtherSurfaceProductChange,
  } = useTradeCaptureModalController({
    open,
    onClose,
    refetchOptionRow,
    output,
    onOtherSurfaceFilled,
    onSurfaceLocked,
  });

  const otherSurfaceProductOptions = useMemo(() => {
    const byId = new Map();
    for (const item of volSurfaceFilterShortcuts ?? []) {
      const id = normalizeProductContractStyleIdValue(
        item?.product_contract_style_id
      );
      if (id == null) continue;
      byId.set(id, {
        value: id,
        label: item?.label || String(id),
      });
    }
    const ocrId = normalizeProductContractStyleIdValue(otherSurfaceProductId);
    if (ocrId != null && !byId.has(ocrId)) {
      const ocrLabel =
        otherSurfaceProductLabel ||
        formatProductContractStyleLabel(otherSurfaceProductStyle) ||
        String(ocrId);
      byId.set(ocrId, { value: ocrId, label: ocrLabel });
    }
    return [...byId.values()];
  }, [
    otherSurfaceProductId,
    otherSurfaceProductLabel,
    otherSurfaceProductStyle,
    volSurfaceFilterShortcuts,
  ]);

  // Both ladder flows read a Call/Put strike grid for one tenor, so they
  // share the tenor header and the screenshot panel. Screenshots are all
  // added on the upload screen (A2-1525), so review has no Add screenshot.
  const isLadder =
    isOtherSurface || isOtherCallPutImport(session?.source || source);
  const screenshotUrl =
    typeof session?.screenshot_url === "string" && session.screenshot_url
      ? session.screenshot_url
      : null;

  const { modalRender, title: draggableTitle } = useDraggableModal(
    open,
    isOtherSurface
      ? "Other Surface Screenshot Import"
      : isLadder
        ? "Open Options"
        : "Option Capture"
  );

  const deriveLadderRows = useCallback(
    (rows) => withOtherSurfaceUnderlying(rows, underlying),
    [underlying]
  );

  const underlyingError = isOtherSurface
    ? (validationErrors || [])
        .map((block) => block?.errors?.underlying)
        .find((messages) => messages?.length)
    : null;

  const underlyingTooltip =
    underlyingError?.join("; ") ||
    (isOtherSurface
      ? "From OCR (futures Last on TFM when applicable). Read notices if settle forward differs."
      : undefined);

  const isAuditTrailSelected =
    importDestination === TRADE_CAPTURE_DESTINATION.AUDIT_TRAIL;

  const importDisabled =
    !session?.is_validated ||
    isBusy ||
    importing ||
    !importDestination ||
    (isAuditTrailSelected && !importDate);

  const closeLabel = session?.status === "imported" ? "Close" : "Cancel";

  const cancelButton = (
    <CancelButton key="cancel" onClick={requestClose} disabled={isBusy}>
      {closeLabel}
    </CancelButton>
  );

  const optionFooterButtons = [
    cancelButton,
    <PrimaryButton
      key="approve"
      onClick={handleApprove}
      loading={validating}
      disabled={isBusy}
    >
      Validate
    </PrimaryButton>,
    <PrimaryButton
      key="import"
      onClick={handleImport}
      loading={importing}
      disabled={importDisabled}
    >
      Import
    </PrimaryButton>,
  ];

  const otherSurfaceFooterButtons = otherSurfacePreview
    ? [
        cancelButton,
        <SecondaryButton
          key="dismiss"
          onClick={dismissOtherSurfacePreview}
          disabled={isBusy}
        >
          Dismiss
        </SecondaryButton>,
        <PrimaryButton
          key="fill"
          onClick={handleFill}
          loading={filling}
          disabled={isBusy}
        >
          Approve
        </PrimaryButton>,
      ]
    : [
        cancelButton,
        <PrimaryButton
          key="preview"
          onClick={handlePreview}
          loading={previewing}
          disabled={isBusy}
        >
          Preview
        </PrimaryButton>,
      ];

  const footerButtons = session
    ? isOtherSurface
      ? otherSurfaceFooterButtons
      : optionFooterButtons
    : [
        <CancelButton key="cancel" onClick={requestClose}>
          Cancel
        </CancelButton>,
      ];

  const steps = isOtherSurface ? OTHER_SURFACE_STEPS : OPTION_STEPS;

  return (
    <>
      <Modal
        title={draggableTitle}
        modalRender={modalRender}
        open={open}
        onCancel={requestClose}
        width={"98vw"}
        getContainer={"#content-root"}
        footer={footerButtons}
        destroyOnClose
        className={`trade-capture__container ${
          dark_Theme ? "ant-modal-dark content_dark" : "ant-modal-light"
        }`}
      >
        <Steps current={stepsCurrent} size="small" style={{ marginBottom: 16 }}>
          {steps.map((step) => (
            <Step
              key={step.title}
              title={step.title}
              description={step.description}
            />
          ))}
        </Steps>

        {!session ? (
          <UploadForm
            onSubmit={handleUpload}
            loading={loading}
            source={source}
            onSourceChange={setSource}
            output={output}
          />
        ) : (
          <div className="trade-capture__content">
            <Spin
              spinning={addingScreenshot || previewing || filling}
              tip={
                addingScreenshot
                  ? "Reading the screenshot. This can take up to a minute…"
                  : filling
                    ? "Filling the Other surface…"
                    : "Preparing the preview…"
              }
            >
              <div className="trade-capture__workspace">
                <div className="trade-capture__workspace-main">
                  {isLadder && (session.tenor || isOtherSurface) && (
                    <div className="trade-capture__review-header">
                      {session.tenor ? (
                        <h2 className="trade-capture__tenor">{session.tenor}</h2>
                      ) : (
                        <span />
                      )}
                      {isOtherSurface && (
                        <div className="trade-capture__review-meta">
                          <label className="trade-capture__product">
                            Product
                            <Select
                              size="small"
                              showSearch
                              optionFilterProp="label"
                              value={otherSurfaceProductId ?? undefined}
                              options={otherSurfaceProductOptions}
                              onChange={(value) => {
                                const shortcut = (
                                  volSurfaceFilterShortcuts ?? []
                                ).find(
                                  (item) =>
                                    normalizeProductContractStyleId(
                                      item?.product_contract_style_id
                                    ) ===
                                    normalizeProductContractStyleId(value)
                                );
                                handleOtherSurfaceProductChange(
                                  value,
                                  shortcut
                                    ? {
                                        id: value,
                                        commodity: shortcut.commodity ?? "",
                                        contract_style:
                                          shortcut.contract_style ?? "",
                                      }
                                    : { id: value }
                                );
                              }}
                              disabled={isBusy || !!otherSurfacePreview}
                              aria-label="Product contract style"
                            />
                          </label>
                          <label className="trade-capture__underlying">
                            UL
                            <Tooltip
                              title={underlyingTooltip}
                              color={underlyingError ? "#ff4d4f" : undefined}
                            >
                              <Input
                                size="small"
                                inputMode="decimal"
                                value={underlying}
                                status={underlyingError ? "error" : undefined}
                                onChange={(e) =>
                                  handleUnderlyingChange(e.target.value)
                                }
                                disabled={isBusy || !!otherSurfacePreview}
                                aria-label="Underlying (UL)"
                              />
                            </Tooltip>
                          </label>
                        </div>
                      )}
                    </div>
                  )}

                  {notices?.length > 0 && !addingScreenshot && (
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message={
                        notices.length === 1 ? (
                          notices[0]
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: 18 }}>
                            {notices.map((notice) => (
                              <li key={notice}>{notice}</li>
                            ))}
                          </ul>
                        )
                      }
                    />
                  )}

                  {!isOtherSurface &&
                    session.is_validated &&
                    !addingScreenshot && (
                    <Alert
                      message="Table validated successfully"
                      type="success"
                      showIcon
                      banner
                      style={{ marginBottom: 12 }}
                    />
                  )}

                  {isLadder && screenshotUrl && (
                    <Collapse className="trade-capture__screenshot-collapse">
                      <Collapse.Panel
                        key="screenshot"
                        header="Show screenshot (check prices against it before submitting)"
                      >
                        <div className="trade-capture__screenshot">
                          <Image
                            src={screenshotUrl}
                            alt="Call/Put ladder screenshot"
                          />
                          <p className="trade-capture__screenshot-note">
                            A price can be read from the row above or below.
                          </p>
                        </div>
                      </Collapse.Panel>
                    </Collapse>
                  )}
                  <div className="trade-capture__review">
                    <div className="trade-capture__review-main">
                      {otherSurfacePreview && (
                        <OtherSurfacePreview preview={otherSurfacePreview} />
                      )}
                      {/* Kept mounted under the preview so Dismiss returns to
                          the user's unsaved edits. */}
                      <div hidden={!!otherSurfacePreview}>
                        <TradeTable
                          tableData={session.table_data || []}
                          validationErrors={validationErrors}
                          onTableUpdate={handleTableUpdate}
                          disabled={isBusy || importing}
                          sessionId={session.id}
                          normalizeRows={normalizeRows}
                          {...(isOtherSurface && {
                            columns: OTHER_SURFACE_LADDER_COLUMNS,
                            deriveDisplayRows: deriveLadderRows,
                          })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Spin>

            {!isOtherSurface && (
              <>
                <Divider />

                <div className="trade-capture__import-section">
                  <label className="trade-capture__import-label">
                    Import Destination:
                  </label>
                  <Radio.Group
                    value={importDestination}
                    onChange={(e) => setImportDestination(e.target.value)}
                    disabled={addingScreenshot}
                  >
                    <Space direction="vertical">
                      <Radio value={TRADE_CAPTURE_DESTINATION.PRICER}>
                        Import to Pricer
                      </Radio>
                      <Radio value={TRADE_CAPTURE_DESTINATION.AUDIT_TRAIL}>
                        Push to Audit Trail
                      </Radio>
                    </Space>
                  </Radio.Group>
                </div>

                {isAuditTrailSelected && (
                  <div className="trade-capture__date-section">
                    <label className="trade-capture__date-label">
                      Date <span style={{ color: "red" }}>*</span>
                    </label>
                    <DatePicker
                      value={importDate}
                      onChange={setImportDate}
                      format="YYYY-MM-DD"
                      disabled={addingScreenshot}
                      className={
                        dark_Theme
                          ? "other-date-picker-dark"
                          : "other-date-picker-light"
                      }
                      popupClassName={
                        dark_Theme ? "date-picker-dropdown-dark" : ""
                      }
                      style={{ width: 200 }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      <VolCalcFailedModal
        open={!!volCalcFailedInfo}
        info={volCalcFailedInfo}
        loading={importing}
        onRetry={confirmImportSkippingVol}
        onCancel={dismissVolCalcFailed}
      />
    </>
  );
};

export default TradeCaptureModal;
